import {
  BirthdayIcon,
  Button,
  CheckmarkIcon,
  CloseIcon,
  LocationIcon,
  MailIcon,
  PhoneIcon,
  TimeIcon,
  UserIcon,
} from '@calizahq/react-components-v2'
import React, { FC, SVGProps, useState } from 'react'

import { BottomSheet, Tag } from '@/components'
import { Task as TaskMutation } from '@/graphql/mutations/task.mutations'
import { useGraphqlMutation } from '@/hooks/useGraphql'
import { useGuardedNavigate } from '@/hooks/useGuardedNavigate'
import { PlannerItemResponse, ScheduledPlannerItem } from '@/types/Planner'
import { Status as TaskStatus, TaskType } from '@/types/Task'
import { formatDate, getPlannerTimeRange, parseDate } from '@/util'
import { truncateString } from '@/util/GeneralUtil'
import { getPlannerItemTitle } from '@/util/PlannerUtil'

import Badge from '../Badge'
import RescheduleForm from './RescheduleForm'

type Props = {
  item: ScheduledPlannerItem | PlannerItemResponse | null
  onClose: () => void
  refetchPlanner: () => void
}

const Attribute = ({ Icon, value }: { Icon: FC<SVGProps<SVGSVGElement>>; value: string | undefined | null }) => {
  if (!value) return null
  return (
    <div className="mb-4 flex items-center">
      <Icon className="mr-3 w-5 icon-secondary" />
      <div className="text-sm text-grey-700">{value}</div>
    </div>
  )
}

const PlannerItemDetail = ({ item, onClose: onCloseSheet, refetchPlanner }: Props) => {
  const navigate = useGuardedNavigate()
  const [showForm, setShowForm] = useState(false)
  const { mutate: completeTask } = useGraphqlMutation(TaskMutation.updateTasks(), {
    onSuccess: () => {
      refetchPlanner()
    },
  })
  const isTask = item?.recordType === 'task'

  const onClose = () => {
    setShowForm(false)
    onCloseSheet()
  }

  const onFormSuccess = () => {
    refetchPlanner()
    onClose()
  }

  const onCancel = () => {
    setShowForm(false)
  }

  const onShowForm = () => {
    setShowForm(true)
  }

  const onView = () => {
    switch (item?.recordType) {
      case 'reservation':
        navigate(`/reservations/${item?.reservation?.id}`)
        return
      case 'task':
        if (item?.type === TaskType.LANDING_VERIFIED_WALKTHROUGH || item?.type === TaskType.LANDING_DIRECT_TOUR) {
          navigate(`/properties/${item?.home?.property?.id}`)
          return
        } else {
          navigate(`/reservations/${item?.reservation?.id}`)
          return
        }
      case 'task manager':
        if (item?.type === TaskType.TURNOVER_HOME_INSPECTION) {
          navigate(`/homes/${item?.homeId}`)
        }
      default:
        return
    }
  }

  const onCompleteTask = () => {
    completeTask({
      taskIds: [item?.recordId],
      task: {
        status: 'complete',
      },
    })
  }

  const renderHeader = () => {
    if (!item) return null
    return (
      <div className="mr-4 pb-4 pt-5">
        <div className="mb-1 text-lg capitalize" style={{ fontWeight: 700 }}>
          {truncateString(getPlannerItemTitle(item as PlannerItemResponse), 30)}
        </div>
        <div className="flex items-center">
          <Badge item={item as PlannerItemResponse} />
          {!item?.isScheduled && (
            <Tag className="ml-2" size="small" variant="error">
              Unscheduled
            </Tag>
          )}
        </div>
      </div>
    )
  }

  return (
    <BottomSheet isVisible={!!item} onClose={onClose} renderHeader={renderHeader}>
      {showForm ? (
        <RescheduleForm item={item} onCancel={onCancel} onSuccess={onFormSuccess} />
      ) : (
        <div className="px-6">
          <div className="mb-4 text-xs text-grey-500">Details</div>
          <Attribute Icon={LocationIcon} value={item?.home?.searchDocument} />
          {isTask &&
          (item?.type === TaskType.LANDING_VERIFIED_WALKTHROUGH || item?.type === TaskType.LANDING_DIRECT_TOUR) ? (
            <div>
              <Attribute Icon={UserIcon} value={item?.home?.property?.directPartnerUser?.fullName} />
              <Attribute Icon={MailIcon} value={item?.home?.property?.directPartnerUser?.email} />
              <Attribute Icon={PhoneIcon} value={item?.home?.property?.directPartnerUser?.phone} />
            </div>
          ) : (
            <Attribute Icon={UserIcon} value={item?.reservation?.member?.fullName} />
          )}
          <Attribute Icon={BirthdayIcon} value={formatDate(parseDate(item?.dueDate), 'MM/dd/yyyy')} />
          <Attribute Icon={TimeIcon} value={getPlannerTimeRange(item?.startTime, item?.endTime)} />
          {isTask && item?.status === TaskStatus.COMPLETE && <Attribute Icon={CheckmarkIcon} value="Complete" />}
          {isTask && item?.status === TaskStatus.INCOMPLETE && <Attribute Icon={CloseIcon} value="Incomplete" />}
          {isTask && item?.type === TaskType.LANDING_VERIFIED_WALKTHROUGH && (
            <div className="mb-2">
              <a
                className="text-action-primary underline"
                href={`https://docs.google.com/forms/d/1E1SlcNVGEmcYTV5aR7oFCduGe9qIMzv5vi8K7U5osm0/viewform?edit_requested=true`}
                rel="noreferrer"
                target="_blank"
              >
                Verified Checklist
              </a>
            </div>
          )}
        </div>
      )}
      {!showForm && (
        <div className="flex flex-wrap items-center border-t border-grey-100 px-6 pt-4">
          {item?.scheduleType === 'flexible' && (
            <Button className="flex-1 bg-grey-100" onClick={onShowForm} variant="secondary">
              {item?.isScheduled ? 'Reschedule' : 'Schedule'}
            </Button>
          )}
          <Button className="ml-1 flex-1 bg-grey-100" onClick={onView} variant="secondary">
            View Details
          </Button>
          {isTask && item?.status !== TaskStatus.COMPLETE && (
            <Button
              className="ml-1 mt-2 flex-1 bg-text-semantic-black hover:bg-text-semantic-black"
              onClick={onCompleteTask}
            >
              Complete
            </Button>
          )}
        </div>
      )}
    </BottomSheet>
  )
}

export default PlannerItemDetail
